<template>
  <div>
    <h3 class="pt-3">{{ title }}</h3>
    <p>{{ introductionText }}</p>
    <b-card no-body class="mt-3">
      <b-tabs ref="tabs" v-model="currentPage" card>
        <b-tab
          ref="info"
          :title="$t('complaint.information')"
          :disabled="complaintCreated || uploading"
        >
          <p>{{ instructionText }}</p>
          <hr />
          <b-form no-validate>
            <b-row>
              <b-col cols="12">
                <b-form-group>
                  {{ $t("complaint.fields.anonymous") }}
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="
                      complaint.anonymous ? 'danger' : 'secondary'
                    "
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto ml-1"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="complaint.anonymous ? 'secondary' : 'info'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.fullName">
                  <b-form-input
                    v-model="complaint.denouncer.fullName"
                    v-validate="rules.denouncer.fullName"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidFullName"
                    name="fullName"
                    :state="validateState('fullName')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidFullName">
                    {{ errors.first("fullName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.position">
                  <b-form-input
                    v-model="complaint.denouncer.position"
                    v-validate="rules.denouncer.position"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPosition"
                    name="position"
                    :state="validateState('position')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPosition">
                    {{ errors.first("position") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.email">
                  <b-form-input
                    v-model="complaint.denouncer.email"
                    v-validate="rules.denouncer.email"
                    type="email"
                    class="form-control"
                    aria-describedby="invalidEmail"
                    name="email"
                    :state="validateState('email')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidEmail">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.phone">
                  <b-form-input
                    ref="phone"
                    v-model="complaint.denouncer.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.denouncer.phone"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :state="validateState('phone')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="4" md="6">
                <b-form-group :label="i18n.businessGroup">
                  <b-form-select
                    v-model="complaint.classification.businessGroup"
                    v-validate="rules.businessGroup"
                    :options="businessGroups"
                    aria-describedby="invalidBusinessGroup"
                    name="businessGroup"
                    :state="validateState('businessGroup')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidBusinessGroup">
                    {{ errors.first("businessGroup") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4" md="6">
                <b-form-group :label="i18n.relatedProject">
                  <b-form-input
                    v-model="complaint.relatedProject"
                    v-validate="rules.relatedProject"
                    class="form-control"
                    aria-describedby="invalidRelatedProject"
                    name="relatedProject"
                    :state="validateState('relatedProject')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidRelatedProject">
                    {{ errors.first("relatedProject") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4" md="6">
                <b-form-group :label="i18n.relatedOffice">
                  <b-form-input
                    v-model="complaint.relatedOffice"
                    v-validate="rules.relatedOffice"
                    class="form-control"
                    aria-describedby="invalidRelatedOffice"
                    name="relatedOffice"
                    :state="validateState('relatedOffice')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidRelatedOffice">
                    {{ errors.first("relatedOffice") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4" md="6">
                <b-form-group :label="$t('complaint.fields.city.label')">
                  <b-form-input
                    v-model="complaint.location"
                    v-validate="rules.location"
                    class="form-control"
                    aria-describedby="invalidLocation"
                    name="complaintLocation"
                    :state="validateState('complaintLocation')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidLocation">
                    {{ errors.first("complaintLocation") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="4" md="6">
                <b-form-group :label="i18n.communicationType">
                  <b-form-select
                    v-model="complaint.classification.level"
                    v-validate="rules.communicationType"
                    :options="levels"
                    aria-describedby="invalidLevel"
                    name="level"
                    :state="validateState('level')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalidLevel">
                    {{ errors.first("level") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  :label="$t('complaint.fields.communication.label')"
                  :description="$t('complaint.fields.text.desc')"
                >
                  <b-form-textarea
                    v-model="complaint.text"
                    v-validate="rules.text"
                    class="form-control"
                    aria-describedby="invalidText"
                    name="communication"
                    :rows="4"
                    :state="validateState('communication')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidText">
                    {{ errors.first("communication") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab
          :title="$t('complaint.files')"
          :disabled="(!infoValidated || complaintCreated) && !uploading"
        >
          <h5 class="pt-1 pb-1">{{ $t("complaint.filesInstruction") }}</h5>
          <multiple-file-upload
            ref="upload"
            :post-action="upload.postAction"
            :data="upload.data"
            :disabled="uploading || uploadComplete"
            @upload-complete="onUploadComplete"
          ></multiple-file-upload>
        </b-tab>
        <b-tab
          :title="$t('complaint.confirmation')"
          :disabled="!infoValidated || uploading"
        >
          <template v-if="complaintCreated">
            <h3>{{ $t("complaint.success") }}</h3>
            <h5>{{ $t("complaint.successInfo") }}</h5>
            <h5>
              {{ $t("labels.protocol") }}:
              <strong>{{ context.protocol }}</strong>
            </h5>
          </template>

          <template v-else>
            <p>{{ $t("complaint.termsInfo") }}</p>
            <b-form-group>
              <b-form-checkbox
                v-model="complaint.termsAccepted"
                v-validate="rules.termsAccepted"
                class="mb-auto"
                style="white-space: pre-line"
                :value="true"
                :unchecked-value="false"
                name="termsAccepted"
                :state="validateState('termsAccepted')"
                >{{ $t("complaint.fields.termsAccepted") }}
              </b-form-checkbox>
            </b-form-group>
            <b-form-group v-if="!complaint.anonymous">
              <b-form-checkbox
                v-model="complaint.dataConsentAccepted"
                v-validate="rules.dataConsentAccepted"
                class="mb-auto"
                :value="true"
                :unchecked-value="false"
                name="dataConsentAccepted"
                :state="validateState('dataConsentAccepted')"
                >{{ $t("complaint.fields.dataConsentAccepted") }}
              </b-form-checkbox>
            </b-form-group>
          </template>
        </b-tab>
      </b-tabs>
      <template slot="footer">
        <template v-if="complaintCreated">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="$router.replace('/')"
            ><i class="fas fa-home" aria-hidden="true"></i> Início
          </b-button>
        </template>
        <template v-else>
          <b-button class="mr-1" :disabled="loading" @click="previousPage">{{
            $t("complaint.back")
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="
              loading || (currentPage === 1 && $refs.upload.hasInvalidFiles)
            "
            @click="nextPage"
          >
            {{
              currentPage === 2 ? $t("complaint.finish") : $t("complaint.next")
            }}
          </b-button>
        </template>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ValidatorDictionary } from "@/i18n";
import { mask } from "vue-the-mask";
import MultipleFileUpload from "../../../components/MultipleFileUpload";
import { sendComplaint, getBusinessGroups, getLevels } from "@/api/main";

export default {
  name: "CompanyComplaint",
  directives: { mask },
  components: { MultipleFileUpload },
  data() {
    return {
      complaint: {
        termsAccepted: false,
        dataConsentAccepted: false,
        anonymous: true,
        denouncer: {},
        classification: {},
      },
      currentPage: 0,
      totalPages: 3,
      infoValidated: false,
      loading: false,
      uploading: false,
      uploadComplete: false,
      upload: {
        postAction: "",
        data: {},
      },
      context: {
        protocol: null,
      },
      complaintCreated: false,
      businessGroups: [],
      levels: [],
    };
  },
  computed: {
    ...mapGetters(["company", "portal"]),
    title() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.title
      )
        return portal.languages[lang].complaint.title;
      return this.$i18n.t("complaint.titleAlt");
    },
    introductionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.introductionText
      )
        return portal.languages[lang].complaint.introductionText;
      return this.$i18n.t("complaint.introductionAlt", [
        this.company.fantasyName,
      ]);
    },
    instructionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.instructionText
      )
        return portal.languages[lang].complaint.instructionText;
      return this.$i18n.t("complaint.instruction");
    },
    rules() {
      return {
        denouncer: {
          fullName: { required: !this.complaint.anonymous },
          position: { required: !this.complaint.anonymous },
          email: { required: !this.complaint.anonymous, email: true },
          phone: { required: false },
        },
        businessGroup: { required: true },
        relatedProject: { required: true },
        relatedOffice: { required: false },
        location: { required: true },
        text: { required: true, max: 700 },
        communicationType: { required: true },
        termsAccepted: { required: true, acceptance: true },
        dataConsentAccepted: {
          required: !this.complaint.anonymous,
          acceptance: true,
        },
      };
    },

    i18n() {
      return ValidatorDictionary[this.$i18n.locale].attributes;
    },
  },

  async created() {
    await this.loadOptionsData();
    const updateEls = ["phone"];

    this.$nextTick(() => {
      for (const id of updateEls) {
        this.$refs[id].$el.oninput({ isTrusted: true });
      }
      this.$validator.reset();
    });
  },

  methods: {
    async submitComplaint() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.complaint));
        if (payload.denouncer.phone) {
          payload.denouncer.phone = payload.denouncer.phone.replace(
            /[^\d]/g,
            ""
          );
          if (!payload.denouncer.phone) delete payload.denouncer.phone;
        }

        const response = await sendComplaint(this.portal.slug, payload);
        if (response.status === 201) {
          const { protocol } = response.data;
          const files = this.$refs.upload.selectedFiles();
          this.context = {
            protocol,
          };
          if (files.length > 0) {
            this.uploading = true;

            this.upload.postAction = `/api/complaints/${this.portal.slug}/${protocol}/files`;
            for (const file of files) {
              file.postAction = this.upload.postAction;
              file.headers = this.upload.headers;
            }

            this.$nextTick(() => this.$refs.upload.start());
          } else {
            this.onComplaintCreated(protocol);
          }
        }
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onUploadComplete() {
      this.uploadComplete = true;
      this.uploading = false;
      this.$nextTick(() => {
        this.currentPage = 2;
        this.$nextTick(() => {
          this.onComplaintCreated(this.context.protocol);
        });
      });
    },

    onComplaintCreated() {
      this.$bvToast.toast(this.$i18n.t("complaint.success"), {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.loading = false;
      this.complaintCreated = true;
    },
    async nextPage() {
      const next = () => {
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = 0;
        }
      };

      if (this.currentPage === 0) {
        let valid = await this.$validator.validateAll();
        const ignoreFields = ["termsAccepted"];
        if (!this.complaint.anonymous) {
          ignoreFields.push("dataConsentAccepted");
        }
        const errors = this.errors.items.filter(
          (item) => ignoreFields.indexOf(item.field) === -1
        );
        valid = valid || errors.length === 0;
        this.infoValidated = valid;
        if (valid) {
          this.$nextTick(next);
        }
      } else if (this.currentPage === 1) {
        if (!this.$refs.upload.hasInvalidFiles) {
          next();
        }
      } else if (this.currentPage === 2) {
        if (await this.$validator.validateAll()) {
          await this.submitComplaint();
        }
      } else {
        next();
      }
    },
    previousPage() {
      this.currentPage--;
      if (this.currentPage < 0) {
        this.currentPage = this.totalPages - 1;
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$router.go(-1);
    },

    async loadOptionsData() {
      await Promise.all([
        getBusinessGroups(this.portal.slug).then(
          (response) => (this.businessGroups = response.data)
        ),
        getLevels(this.portal.slug).then(
          (response) => (this.levels = response.data)
        ),
      ]);
    },
  },
};
</script>
